import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

/**
 * Reusable hook for fetching real YouTube creators with caching and filtering
 * @param {Object} options
 * @param {number} options.minSubscribers - Minimum subscriber count (default: 10000)
 * @param {number} options.limit - Max creators to fetch (default: 6)
 * @param {boolean} options.sortBySubscribers - Sort by subscriber count DESC (default: true)
 * @param {boolean} options.autoFetch - Auto-fetch on mount with default niche (default: false)
 */
export const useRealCreators = (options = {}) => {
  const {
    minSubscribers = 10000,
    limit = 6,
    sortBySubscribers = true,
    autoFetch = false
  } = options;

  const [creators, setCreators] = useState([]);
  const [selectedNiche, setSelectedNiche] = useState('All Niches');
  const [selectedRegion, setSelectedRegion] = useState('');  // Empty = Global
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiNote, setApiNote] = useState(null);  // Note from API (e.g., quota exceeded)
  const [regionFilterActive, setRegionFilterActive] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);  // Timestamp when cache was last updated
  const [niches, setNiches] = useState([]);
  const [nichesLoading, setNichesLoading] = useState(true);
  
  // Cache results per niche+region to avoid redundant API calls
  const cacheRef = useRef({});
  
  // Helper function to format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Updated just now';
      if (diffMins < 60) return `Updated ${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `Updated ${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      return `Updated ${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } catch (e) {
      return null;
    }
  };

  // Fetch niches dynamically from brands API
  const loadNiches = useCallback(async () => {
    setNichesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/brands?limit=500`);
      const brands = response.data.brands || [];
      
      // Extract unique niches and count brands per niche
      const nicheCounts = {};
      brands.forEach(brand => {
        const niche = brand.niche_category;
        if (niche && niche !== 'Unknown') {
          nicheCounts[niche] = (nicheCounts[niche] || 0) + 1;
        }
      });
      
      // Sort by brand count (descending)
      const sortedNiches = Object.entries(nicheCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([niche]) => niche);
      
      setNiches(sortedNiches);
    } catch (err) {
      console.error('Failed to load niches:', err);
      // Fallback to default niches if API fails
      setNiches([
        'Technology',
        'Gaming',
        'Creator Tools',
        'E-commerce',
        'Education'
      ]);
    } finally {
      setNichesLoading(false);
    }
  }, []);

  // Fetch creators for a specific niche and region
  const fetchCreators = useCallback(async (niche = selectedNiche, region = selectedRegion) => {
    // Check cache first
    const cacheKey = `${niche}-${region}-${limit}-${minSubscribers}`;
    if (cacheRef.current[cacheKey]) {
      setCreators(cacheRef.current[cacheKey].creators);
      setApiNote(cacheRef.current[cacheKey].note);
      setRegionFilterActive(cacheRef.current[cacheKey].regionFilterActive);
      setLastUpdated(cacheRef.current[cacheKey].lastUpdated);
      return cacheRef.current[cacheKey].creators;
    }
    
    setLoading(true);
    setError(null);
    setApiNote(null);
    setLastUpdated(null);
    
    try {
      // For "All Niches", use a general search query with higher limit
      const nicheQuery = niche === 'All Niches' ? 'popular youtube creators' : niche;
      const requestLimit = niche === 'All Niches' ? 50 : limit;  // Higher limit for All Niches
      let url = `${API}/youtube/search-creators?niche=${encodeURIComponent(nicheQuery)}&limit=${requestLimit}`;
      
      // Add region code if selected (not Global)
      if (region) {
        url += `&regionCode=${region}`;
      }
      
      const response = await axios.get(url);
      
      let data = response.data.creators || [];
      const note = response.data.note || null;
      const regionApplied = response.data.region_filter_applied !== false;
      const updatedAt = response.data.last_updated || null;
      
      // Apply quality filtering: only show creators with >= minSubscribers
      data = data.filter(creator => 
        creator.subscriber_count && creator.subscriber_count >= minSubscribers
      );
      
      // Sort by subscriber count DESC if enabled
      if (sortBySubscribers) {
        data.sort((a, b) => (b.subscriber_count || 0) - (a.subscriber_count || 0));
      }
      
      setCreators(data);
      setApiNote(note);
      setRegionFilterActive(regionApplied);
      setLastUpdated(updatedAt);
      
      // If region filter couldn't be applied, reset to Global to avoid confusion
      if (region && !regionApplied) {
        setSelectedRegion('');
      }
      
      // Cache the results with metadata
      cacheRef.current[cacheKey] = {
        creators: data,
        note: note,
        regionFilterActive: regionApplied,
        lastUpdated: updatedAt
      };
      
      return data;
    } catch (err) {
      console.error('Failed to load creators:', err);
      setError('Unable to load creators. Please try again.');
      setCreators([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedNiche, selectedRegion, limit, minSubscribers, sortBySubscribers]);

  // Handle niche selection change
  const handleNicheChange = useCallback((niche) => {
    setSelectedNiche(niche);
  }, []);

  // Handle region selection change
  const handleRegionChange = useCallback((region) => {
    setSelectedRegion(region);
  }, []);

  // Clear cache for a specific niche+region (for retry functionality)
  const clearCache = useCallback((niche, region) => {
    if (niche && region !== undefined) {
      // Clear specific cache
      const cacheKey = `${niche}-${region}-${limit}-${minSubscribers}`;
      delete cacheRef.current[cacheKey];
    } else {
      // Clear all cache
      cacheRef.current = {};
    }
  }, [limit, minSubscribers]);

  // Load niches on mount
  useEffect(() => {
    loadNiches();
  }, [loadNiches]);

  // Fetch creators when niche or region changes
  useEffect(() => {
    if (!nichesLoading || autoFetch) {
      fetchCreators(selectedNiche, selectedRegion);
    }
  }, [selectedNiche, selectedRegion, nichesLoading, autoFetch, fetchCreators]);

  return {
    creators,
    loading,
    error,
    apiNote,
    regionFilterActive,
    lastUpdated,
    lastUpdatedFormatted: formatRelativeTime(lastUpdated),
    niches,
    nichesLoading,
    selectedNiche,
    selectedRegion,
    setSelectedNiche: handleNicheChange,
    setSelectedRegion: handleRegionChange,
    fetchCreators,
    clearCache,
    refetch: () => {
      clearCache(selectedNiche, selectedRegion);
      fetchCreators(selectedNiche, selectedRegion);
    }
  };
};

export default useRealCreators;
